/**
 * FormStepper Component - Story 6.3: Create Add Product Form
 * Step indicator component showing form progress
 */

import { Check } from 'lucide-react'
import { FORM_STEPS } from '../../constants/productFormConstants'
import { cn } from '@/lib/utils'
import './FormStepper.css'

export interface FormStepperProps {
  steps: typeof FORM_STEPS
  currentStep: number
  totalSteps: number
}

/**
 * FormStepper Component
 * Visual progress indicator showing current step in multi-step form
 *
 * @component
 * @example
 * <FormStepper steps={FORM_STEPS} currentStep={2} totalSteps={5} />
 */
export function FormStepper({ steps, currentStep, totalSteps }: FormStepperProps) {
  return (
    <div className="form-stepper" aria-label="Form progress">
      <div className="steps-container">
        {steps.map((step, index) => {
          const stepNumber = step.step
          const isActive = stepNumber === currentStep
          const isCompleted = stepNumber < currentStep

          return (
            <div key={stepNumber} className="step-wrapper">
              {/* Step Circle */}
              <div
                className={cn(
                  'step-circle',
                  isActive && 'active',
                  isCompleted && 'completed'
                )}
                aria-current={isActive ? 'step' : undefined}
              >
                {isCompleted ? (
                  <Check size={20} className="step-icon" aria-hidden="true" />
                ) : (
                  <span className="step-number">{stepNumber}</span>
                )}
              </div>

              {/* Step Label (visible on larger screens) */}
              <div className="step-label">
                <p className="step-title">{step.title}</p>
                <p className="step-description">{step.description}</p>
              </div>

              {/* Connector Line */}
              {index < steps.length - 1 && (
                <div
                  className={cn(
                    'step-connector',
                    stepNumber < currentStep && 'completed'
                  )}
                  aria-hidden="true"
                />
              )}
            </div>
          )
        })}
      </div>

      {/* Progress Bar */}
      <div className="progress-bar" role="progressbar" aria-valuenow={currentStep} aria-valuemin={1} aria-valuemax={totalSteps}>
        <div
          className="progress-fill"
          style={{
            width: `${(currentStep / totalSteps) * 100}%`,
          }}
        />
      </div>

      {/* Mobile Step Counter */}
      <div className="mobile-step-counter">
        Step {currentStep} of {totalSteps}
      </div>
    </div>
  )
}
